import { useProjectData } from "@/contexts/ProjectData"
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetOverlay,
} from "@fieldbook/ui/sheet"
import { cn } from "@fieldbook/ui/utils"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { XIcon } from "lucide-react"
import { useCallback, useRef } from "react"
import { useSuspendSearchMode } from "@/contexts/SearchMode"

function DataSheetContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content>) {
  return (
    <SheetPrimitive.Portal data-slot="sheet-portal">
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          className,
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="data-[state=open]:bg-secondary rounded-xs focus:outline-hidden absolute right-4 top-4 opacity-70 transition-opacity focus:opacity-100 disabled:pointer-events-none">
          <XIcon className="size-6" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  )
}

export const Route = createFileRoute("/_private/projects/$id/data")({
  component: DataSheetLayout,
})

function DataSheetLayout() {
  useSuspendSearchMode()
  const navigate = useNavigate()
  const { id } = Route.useParams()
  const {
    data: { project },
  } = useProjectData()

  const scrollRef = useRef<HTMLDivElement>(null)
  // Tap the sheet title (which sits above the scroll region and
  // therefore stays visible) to send the panel back to the top. Works
  // for whichever data panel is currently rendered through the Outlet.
  const handleScrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  return (
    <Sheet
      open={true}
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          navigate({ to: "/projects/$id", params: { id }, replace: true })
        }
      }}
    >
      <DataSheetContent className="sm:w-135 w-screen p-2">
        <SheetHeader className="flex-row items-center gap-2 p-0 pr-10">
          <SheetTitle
            onClick={handleScrollToTop}
            className="min-w-0 flex-1 cursor-pointer truncate text-xl font-medium"
            title="Scroll to top"
          >
            <div className="flex-col">
              {project?.name}
              <div className="font-mono text-sm">{project?.code}</div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-scroll">
          <Outlet />
        </div>
      </DataSheetContent>
    </Sheet>
  )
}
